import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ExecuteSalesMoveRequest } from "@sales-ops-amplify/shared";
import {
  executeSalesMoveAction,
  listSalesMoveActions,
  listSalesMoves,
  listSalesMovesByDeals,
  type ListSalesMovesParams,
} from "@/api/sales-moves";
import { ApiError } from "@/api/client";

export function useSalesMoves(params?: ListSalesMovesParams) {
  return useQuery({
    queryKey: ["sales-moves", params],
    queryFn: () => listSalesMoves(params),
    refetchInterval: 60_000,
  });
}

export function useSalesMovesByDeals(dealIds: string[], enabled = true) {
  const key = [...dealIds].sort().join(",");
  return useQuery({
    queryKey: ["sales-moves-by-deals", key],
    queryFn: () => listSalesMovesByDeals(dealIds),
    enabled: enabled && dealIds.length > 0,
  });
}

export function useSalesMoveActions(
  moveId: string | undefined,
  fingerprint: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ["sales-move-actions", moveId, fingerprint],
    queryFn: () =>
      listSalesMoveActions(moveId!, {
        fingerprint,
        limit: 20,
      }),
    enabled: Boolean(enabled && moveId && fingerprint),
  });
}

export function useSalesMoveAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      id: string;
      body: ExecuteSalesMoveRequest;
      idempotencyKey?: string;
    }) => executeSalesMoveAction(args.id, args.body, args.idempotencyKey),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["sales-moves"] });
      void qc.invalidateQueries({ queryKey: ["sales-moves-by-deals"] });
      void qc.invalidateQueries({
        queryKey: ["sales-move-actions", vars.id],
      });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        void qc.invalidateQueries({ queryKey: ["sales-moves"] });
      }
    },
  });
}
